/**
 * A route screen whose chunk can be PRELOADED, rendered without Suspense —
 * TRBUD-295.
 *
 * `App.tsx` starts the landing screen's chunk before the session has resolved,
 * so it is usually loaded by the time the route first renders. `React.lazy`
 * cannot take advantage of that: its factory returns a promise, and a promise
 * never resolves synchronously, so a lazy component ALWAYS suspends on its
 * first render — even when the module is sitting in memory. The fallback then
 * commits, and React's fallback throttle (`FALLBACK_THROTTLE_MS`, 300 ms) holds
 * the real screen back until 300 ms after that fallback appeared, so the
 * content that was ready arrives a third of a second late. Measured on this
 * build before this helper existed: the queue's data request left 300-370 ms
 * after the session answered, against 15-120 ms with the screen bundled
 * eagerly. That is a regression on the screen nearly everybody lands on.
 *
 * So this renders the component SYNCHRONOUSLY once its module has loaded, and
 * otherwise shows the same one-line loading state the Suspense fallback shows,
 * switching to the screen with an ordinary state update when the chunk
 * arrives: nothing suspends, so nothing is throttled.
 *
 * A chunk that fails to load is re-thrown during render, so it reaches the
 * route's `ErrorBoundary` exactly as a failed `lazy()` import does, and the
 * next attempt (a new route, a reset boundary) fetches again rather than
 * replaying a cached failure.
 */
import { createElement, useEffect, useState, type ComponentType } from "react";
import { Loading } from "../components/states";

/** The fallback text, identical to App's Suspense fallback so both read the same. */
export const SCREEN_LOADING_LABEL = "Loading this screen…";

export interface PreloadableScreen<P extends object> {
  Screen: ComponentType<P>;
  /** Start (or join) the chunk fetch. Never rejects; a failure is reported by `Screen`. */
  preload: () => Promise<void>;
  /** Whether the module has loaded — for tests. */
  isLoaded: () => boolean;
}

export function preloadableScreen<P extends object>(
  load: () => Promise<ComponentType<P>>,
): PreloadableScreen<P> {
  let component: ComponentType<P> | null = null;
  let pending: Promise<ComponentType<P>> | null = null;

  const start = (): Promise<ComponentType<P>> => {
    pending ??= load().then(
      (loaded) => {
        component = loaded;
        return loaded;
      },
      (error: unknown) => {
        // Forget the failure, so the next attempt fetches again.
        pending = null;
        throw error;
      },
    );
    return pending;
  };

  function Screen(props: P) {
    const [, setLoaded] = useState(0);
    const [error, setError] = useState<unknown>(null);
    useEffect(() => {
      if (component !== null) return;
      let alive = true;
      start().then(
        () => {
          if (alive) setLoaded((n) => n + 1);
        },
        (failure: unknown) => {
          if (alive) setError(() => failure);
        },
      );
      return () => {
        alive = false;
      };
    }, []);

    if (error !== null) throw error;
    if (component !== null) return createElement(component, props);
    return <Loading label={SCREEN_LOADING_LABEL} />;
  }

  return {
    Screen,
    preload: () => start().then(() => undefined, () => undefined),
    isLoaded: () => component !== null,
  };
}
